/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DependencyList,
  type ReactNode,
} from 'react';

type DashboardHeaderSlotKey = 'context' | 'actions';

interface DashboardHeaderSlot {
  owner: symbol;
  node: ReactNode;
}

type DashboardHeaderSlotState = Partial<Record<DashboardHeaderSlotKey, DashboardHeaderSlot>>;
type DashboardHeaderSlotInput = Partial<Record<DashboardHeaderSlotKey, ReactNode>>;

interface DashboardHeaderContextValue {
  setSlot: (key: DashboardHeaderSlotKey, node: ReactNode, owner: symbol) => void;
  clearSlot: (key: DashboardHeaderSlotKey, owner: symbol) => void;
}

const DashboardHeaderActionsContext = createContext<DashboardHeaderContextValue | null>(null);
const DashboardHeaderSlotsContext = createContext<DashboardHeaderSlotState>({});

export function DashboardHeaderProvider({ children }: { children: ReactNode }) {
  const [slots, setSlots] = useState<DashboardHeaderSlotState>({});

  const setSlot = useCallback((key: DashboardHeaderSlotKey, node: ReactNode, owner: symbol) => {
    setSlots((current) => {
      const existing = current[key];
      if (existing?.owner === owner && existing.node === node) return current;
      return { ...current, [key]: { owner, node } };
    });
  }, []);

  const clearSlot = useCallback((key: DashboardHeaderSlotKey, owner: symbol) => {
    setSlots((current) => {
      if (current[key]?.owner !== owner) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const value = useMemo(() => ({ setSlot, clearSlot }), [clearSlot, setSlot]);

  return (
    <DashboardHeaderActionsContext.Provider value={value}>
      <DashboardHeaderSlotsContext.Provider value={slots}>
        {children}
      </DashboardHeaderSlotsContext.Provider>
    </DashboardHeaderActionsContext.Provider>
  );
}

export function useDashboardHeaderSlots() {
  return useContext(DashboardHeaderSlotsContext);
}

export function useDashboardHeader(slots: DashboardHeaderSlotInput, deps: DependencyList = []) {
  const context = useContext(DashboardHeaderActionsContext);
  const ownerRef = useRef<symbol | null>(null);
  const slotsRef = useRef(slots);
  slotsRef.current = slots;

  if (!ownerRef.current) ownerRef.current = Symbol('dashboard-header-slot-owner');

  useEffect(() => {
    if (!context || !ownerRef.current) return;

    const owner = ownerRef.current;
    const entries = Object.entries(slotsRef.current) as Array<[DashboardHeaderSlotKey, ReactNode]>;

    entries.forEach(([key, node]) => {
      if (node === null || node === undefined || node === false) {
        context.clearSlot(key, owner);
      } else {
        context.setSlot(key, node, owner);
      }
    });

    return () => entries.forEach(([key]) => context.clearSlot(key, owner));
    // JSX nodes are deliberately controlled by the caller-provided dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, ...deps]);
}
